// What the client knows about a room that addresses need (02, "Matrix data rules"; 05, "Where a
// channel opens"). The frame (07) builds its lists on the same rules.
import { ClientEvent, EventType, KnownMembership, ReceiptType, RoomStateEvent, type MatrixClient, type Room } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { roomPath } from '../core/routes';
import { serverOf } from '../core/session';

export const isJoined = (room: Room | null | undefined): room is Room => room?.getMyMembership() === KnownMembership.Join;

/** Draws again on any state change of the room (changes elsewhere show at once). */
export function useRoomState(room: Room | null | undefined) {
  const [, set] = useState(0);
  useEffect(() => {
    if (!room) return;
    const changed = () => set((n) => n + 1);
    room.currentState.on(RoomStateEvent.Events, changed);
    return () => void room.currentState.off(RoomStateEvent.Events, changed);
  }, [room]);
}

/** Draws again when the person's account data of `type` (any type without one) changes. */
export function useAccountData(client: MatrixClient, type?: string) {
  const [, set] = useState(0);
  useEffect(() => {
    const changed = (e: { getType(): string }) => (!type || e.getType() === type) && set((n) => n + 1);
    client.on(ClientEvent.AccountData, changed);
    return () => void client.off(ClientEvent.AccountData, changed);
  }, [client, type]);
}

/** A room's address in paths: its main address when it belongs to it and it wasn't replaced. */
export function refFor(room: Room): string {
  const alias = room.getCanonicalAlias();
  const replaced = room.currentState.getStateEvents(EventType.RoomTombstone, '');
  return alias && !replaced ? alias : room.roomId;
}

/** The joined spaces that list this room as a child (with a `via`). */
export function parentsOf(client: MatrixClient, roomId: string): Room[] {
  return client.getRooms().filter((space) => {
    if (!isJoined(space) || !space.isSpaceRoom()) return false;
    const child = space.currentState.getStateEvents(EventType.SpaceChild, roomId);
    return Array.isArray(child?.getContent().via);
  });
}

export function isDirect(client: MatrixClient, roomId: string): boolean {
  const direct = client.getAccountData(EventType.Direct)?.getContent<Record<string, string[]>>() ?? {};
  return Object.values(direct).some((rooms) => rooms.includes(roomId));
}

/**
 * Where the app opens a joined room by itself (05): a space as a space; a channel in the open space
 * if it is one of its parents, else its first parent; else Direct messages or Home. The best-fitting
 * (canonical) parent joins with the frame (07).
 */
export function pathOf(client: MatrixClient, room: Room, event?: string, openSpace?: string): string {
  if (room.isSpaceRoom()) return `/${encodeURIComponent(refFor(room))}/`;
  const parents = parentsOf(client, room.roomId);
  const space = parents.find((p) => p.roomId === openSpace) ?? parents[0];
  if (space) return roomPath({ space: refFor(space) }, refFor(room), event);
  return roomPath({ direct: isDirect(client, room.roomId) }, refFor(room), event);
}

/** A joined room by its ID or main address. */
export function findRoom(client: MatrixClient, ref: string): Room | undefined {
  if (ref.startsWith('!')) return client.getRoom(ref) ?? undefined;
  return client.getRooms().find((r) => r.getCanonicalAlias() === ref || r.getAltAliases().includes(ref));
}

/** Up to three servers to find a room by: its creator's, then its members' most common ones. */
export function viaServers(room: Room): string[] {
  const creator = room.currentState.getStateEvents(EventType.RoomCreate, '')?.getSender();
  const counts = new Map<string, number>();
  room.getJoinedMembers().forEach((m) => counts.set(serverOf(m.userId), (counts.get(serverOf(m.userId)) ?? 0) + 1));
  const byCount = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([s]) => s);
  return [...new Set([...(creator ? [serverOf(creator)] : []), ...byCount])].slice(0, 3);
}

/** The person's own receipt as the server holds it, the later of public and private (loaded or not). */
export function ownReceipt(room: Room, me: string): string | undefined {
  const receipts = [ReceiptType.Read, ReceiptType.ReadPrivate].map((type) => room.getReadReceiptForUserId(me, true, type)).filter((r) => !!r);
  return receipts.sort((a, b) => b.data.ts - a.data.ts)[0]?.eventId;
}
